// Shared loader for the paginated CPCP V2 ListConfig endpoint.
//
// GET /cpcp/v2/config is paginated: the backend returns at most one DynamoDB
// scan page per call (default Limit 100, and at most 1 MB) and hands back a
// `nextToken` whenever more rows remain. A single call therefore truncates the
// config list, which silently under-populates the Config Manager table and the
// Feature/Customer-Type dropdowns. This helper follows `nextToken` until the
// table is fully drained so callers always get every config.

import { API } from "aws-amplify";

// A ListConfig page. Configs are left as `unknown`-ish records so each caller
// can keep its own narrower ConfigItem shape; the loader only cares about
// concatenating pages and following the token.
interface ListConfigPage<T> {
  configs?: T[];
  nextToken?: string;
}

// Hard stop so a backend bug that echoes the same token can never spin forever.
// Far above any realistic number of config rows / pages.
const MAX_PAGES = 100;

/**
 * Fetch every CPCP V2 config, following `nextToken` across pages.
 *
 * @returns the fully concatenated list of configs from all pages.
 */
export async function fetchAllConfigs<T = unknown>(): Promise<T[]> {
  const all: T[] = [];
  let nextToken: string | undefined;
  let pages = 0;

  do {
    const result: ListConfigPage<T> = await API.get("CpcpV2API", "/cpcp/v2/config", {
      ...(nextToken ? { queryStringParameters: { nextToken } } : {}),
    });
    if (result.configs) {
      all.push(...result.configs);
    }
    nextToken = result.nextToken;
    pages += 1;
  } while (nextToken && pages < MAX_PAGES);

  return all;
}
